import styled, { css, keyframes } from "styled-components";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/Assessment/BasicInfoStep.styles.js
import { DIET_C } from "../../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// BASIC INFO STEP — form field styles
// ═══════════════════════════════════════════════════════════════════════════════

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-5) var(--sp-4);

  @media (max-width: 40em) {
    grid-template-columns: 1fr;
    gap: var(--sp-4);
  }
`;
FormGrid.displayName = "FormGrid";

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  ${({ $fullWidth }) => $fullWidth && css`grid-column: 1 / -1;`}
`;
FieldGroup.displayName = "FieldGroup";

export const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--c-text);

  .optional {
    font-weight: 400;
    color: var(--c-text-muted);
    font-size: 0.8rem;
    margin-left: 4px;
  }
`;
Label.displayName = "Label";

// ─── Section grouping ────────────────────────────────────────────────────────
// Breaks the long flat field list into scannable chunks ("About you",
// "Contact details", "Body measurements") — a plain heading + rule rather
// than a boxed card, so it reads as structure, not another form control.

export const SectionLabel = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin-top: var(--sp-2);
  color: var(--c-text-muted);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${DIET_C.secondary}33;
  }

  &:first-child {
    margin-top: 0;
  }
`;
SectionLabel.displayName = "SectionLabel";

const inputBase = css`
  width: 100%;
  border-radius: var(--r-sm);
  border: 1px solid ${({ $error }) => ($error ? DIET_C.error : `${DIET_C.secondary}88`)};
  background: var(--c-surface);
  color: var(--c-text);
  padding: 12px 14px;
  font-size: 0.95rem;
  transition: border-color var(--t-fast), box-shadow var(--t-fast);

  &:hover:not(:disabled):not(:focus) {
    border-color: ${({ $error }) => ($error ? DIET_C.error : `${DIET_C.secondary}`)};
  }

  &:focus {
    outline: none;
    border-color: ${({ $error }) => ($error ? DIET_C.error : DIET_C.primary)};
    box-shadow: 0 0 0 3px
      ${({ $error }) => ($error ? `${DIET_C.error}22` : `${DIET_C.primary}22`)};
  }

  &::placeholder {
    color: var(--c-text-muted);
  }
`;

export const Input = styled.input`
  ${inputBase}
`;
Input.displayName = "Input";

// Shares inputBase so any remaining plain dropdowns line up with text
// inputs beside them — same height, radius, border, and focus ring.
export const Select = styled.select`
  ${inputBase}
  cursor: pointer;
  appearance: none;
  padding-right: 32px;
  background-image: linear-gradient(45deg, transparent 50%, var(--c-text-muted) 50%),
    linear-gradient(135deg, var(--c-text-muted) 50%, transparent 50%);
  background-position: calc(100% - 18px) calc(50% + 2px), calc(100% - 13px) calc(50% + 2px);
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
`;
Select.displayName = "Select";

// ─── Phone field (country code + national number) ───────────────────────────

export const PhoneRow = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 190px) 1fr;
  gap: var(--sp-3);
  align-items: start;

  /* Let the country picker take a full row of its own on narrow screens
     rather than squeezing the number field. */
  @media (max-width: 30em) {
    grid-template-columns: 1fr;
    gap: var(--sp-2);
  }
`;
PhoneRow.displayName = "PhoneRow";

// ─── Country code combobox (CountryCodeSelect.js) ───────────────────────────

const fadeScaleIn = keyframes`
  from { opacity: 0; transform: translateY(-4px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

export const ComboBoxWrap = styled.div`
  position: relative;
`;
ComboBoxWrap.displayName = "ComboBoxWrap";

export const ComboBoxTrigger = styled.button`
  ${inputBase}
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  text-align: left;
`;
ComboBoxTrigger.displayName = "ComboBoxTrigger";

export const ComboBoxFlag = styled.span`
  font-size: 1.15rem;
  line-height: 1;
`;
ComboBoxFlag.displayName = "ComboBoxFlag";

export const ComboBoxDial = styled.span`
  flex: 1;
  font-weight: 600;
  color: var(--c-text);
`;
ComboBoxDial.displayName = "ComboBoxDial";

export const ComboBoxChevron = styled.span`
  display: inline-flex;
  color: var(--c-text-muted);
  transition: transform var(--t-fast);
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;
ComboBoxChevron.displayName = "ComboBoxChevron";

export const ComboBoxPanel = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  width: max(100%, 280px);
  background: var(--c-surface);
  border: 1px solid ${DIET_C.secondary}55;
  border-radius: var(--r-sm);
  box-shadow: 0 12px 28px -8px rgba(0, 0, 0, 0.28);
  overflow: hidden;
  animation: ${fadeScaleIn} 120ms ease-out;
`;
ComboBoxPanel.displayName = "ComboBoxPanel";

export const ComboBoxSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${DIET_C.secondary}33;
`;
ComboBoxSearchWrap.displayName = "ComboBoxSearchWrap";

export const ComboBoxSearchIcon = styled.span`
  display: inline-flex;
  color: var(--c-text-muted);
  flex-shrink: 0;
`;
ComboBoxSearchIcon.displayName = "ComboBoxSearchIcon";

export const ComboBoxSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--c-text);
  font-size: 0.92rem;

  &::placeholder {
    color: var(--c-text-muted);
  }
`;
ComboBoxSearchInput.displayName = "ComboBoxSearchInput";

export const ComboBoxList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 260px;
  overflow-y: auto;
`;
ComboBoxList.displayName = "ComboBoxList";

export const ComboBoxOption = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: calc(var(--r-sm) - 2px);
  cursor: pointer;
  background: ${({ $active }) => ($active ? `${DIET_C.primary}14` : "transparent")};

  ${({ $selected }) =>
    $selected &&
    css`
      &::after {
        content: "✓";
        margin-left: auto;
        color: ${DIET_C.primary};
        font-weight: 700;
        font-size: 0.8rem;
      }
    `}
`;
ComboBoxOption.displayName = "ComboBoxOption";

export const ComboBoxOptionFlag = styled.span`
  font-size: 1.05rem;
  line-height: 1;
  flex-shrink: 0;
`;
ComboBoxOptionFlag.displayName = "ComboBoxOptionFlag";

export const ComboBoxOptionName = styled.span`
  flex: 1;
  font-size: 0.9rem;
  color: var(--c-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
ComboBoxOptionName.displayName = "ComboBoxOptionName";

export const ComboBoxOptionDial = styled.span`
  font-size: 0.82rem;
  color: var(--c-text-muted);
  flex-shrink: 0;
`;
ComboBoxOptionDial.displayName = "ComboBoxOptionDial";

export const ComboBoxEmpty = styled.p`
  margin: 0;
  padding: 18px 12px;
  text-align: center;
  color: var(--c-text-muted);
  font-size: 0.85rem;
`;
ComboBoxEmpty.displayName = "ComboBoxEmpty";

export const InlineError = styled.p`
  margin: 0;
  color: ${DIET_C.error};
  font-size: 0.82rem;
  line-height: 1.4;
`;
InlineError.displayName = "InlineError";

export const HelperText = styled.p`
  margin: 0;
  color: var(--c-text-muted);
  font-size: 0.8rem;
  line-height: 1.4;
`;
HelperText.displayName = "HelperText";

// ─── Gender select ───────────────────────────────────────────────────────────

export const PillRow = styled.div`
  display: flex;
  gap: var(--sp-2);
  flex-wrap: wrap;
`;
PillRow.displayName = "PillRow";

export const Pill = styled.button`
  flex: 1;
  min-width: 90px;
  border-radius: var(--r-sm);
  border: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}66`)};
  background: ${({ $active }) => ($active ? `${DIET_C.primary}0f` : "var(--c-surface)")};
  color: var(--c-text);
  padding: 11px 14px;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color var(--t-fast), background var(--t-fast), transform 80ms ease-out;

  &:hover {
    border-color: ${DIET_C.primary};
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent);
    outline-offset: 2px;
  }
`;
Pill.displayName = "Pill";

// ─── Unit toggle (cm/ft·in, kg/lb) ──────────────────────────────────────────

export const UnitToggle = styled.div`
  display: inline-flex;
  border-radius: 999px;
  border: 1px solid ${DIET_C.secondary}88;
  padding: 2px;
  gap: 2px;
`;
UnitToggle.displayName = "UnitToggle";

export const UnitButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  background: ${({ $active }) => ($active ? DIET_C.primary : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "var(--c-text-muted)")};
  transition: background var(--t-fast), color var(--t-fast);

  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}33`)};
  }
`;
UnitButton.displayName = "UnitButton";

export const FieldHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
`;
FieldHeaderRow.displayName = "FieldHeaderRow";

// ─── Compound input row (feet + inches) ─────────────────────────────────────

export const SplitInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
`;
SplitInputRow.displayName = "SplitInputRow";

export const SplitInputWrap = styled.div`
  position: relative;

  input {
    padding-right: 34px;
  }

  .unit-suffix {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--c-text-muted);
    font-size: 0.82rem;
    pointer-events: none;
  }
`;
SplitInputWrap.displayName = "SplitInputWrap";

// ─── Blocking notice (age below 13, and age 13-17 per DEC-08 minor policy) ──

export const BlockingNotice = styled.div`
  grid-column: 1 / -1;
  background: ${DIET_C.error}12;
  border: 1px solid ${DIET_C.error}55;
  border-radius: var(--r-sm);
  padding: var(--sp-4) var(--sp-5);
  color: var(--c-text);
  font-size: 0.9rem;
  line-height: 1.5;
`;
BlockingNotice.displayName = "BlockingNotice";

// ─── Soft warning (target weight) ───────────────────────────────────────────

export const WarningBox = styled.div`
  grid-column: 1 / -1;
  background: ${DIET_C.accent}14;
  border: 1px solid ${DIET_C.accent}55;
  border-radius: var(--r-sm);
  padding: var(--sp-4) var(--sp-5);
  color: var(--c-text);
  font-size: 0.88rem;
  line-height: 1.5;
`;
WarningBox.displayName = "WarningBox";